import PrimaryButton from 'components/elements/buttons/PrimaryButton'
import SecondaryButton from 'components/elements/buttons/SecondaryButton'
import CameraIcon from 'components/elements/icons/CameraIcon'
import InlineText from 'components/elements/texts/InlineText'
import { CLIENT_SETTINGS } from 'constants/clientSettings'
import { IMAGE_MAX_SIZE } from 'constants/size'
import EXIF from 'exif-js'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { canAutoRotate } from 'utils/image'

import ItemLine from './ItemLine'
import RequiredOptional from './RequiredOptional'

/**
 * 画像選択行
 */
const SelectImageLine: React.FC<SelectImageLineProps> = (props) => {
  const selectImageRef = useRef<HTMLInputElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [autoRotateSupport, setAutoRotateSupport] = useState(false)

  const onClickSelect = useCallback((): void => {
    selectImageRef.current?.click()
  }, [])

  const onClickDelete = useCallback((): void => {
    if (selectImageRef.current) {
      selectImageRef.current.value = ''
    }
    props.setImage && props.setImage('')
  }, [props])

  const onChangeImage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const image = new Image()
      const reader = new FileReader()

      reader.onload = (): void => {
        image.onload = (): void => {
          const resize = ((): { width: number; height: number } => {
            const { width, height } = image
            if (width < IMAGE_MAX_SIZE && height < IMAGE_MAX_SIZE) {
              return {
                width,
                height,
              }
            } else if (width > height) {
              return {
                width: IMAGE_MAX_SIZE,
                height: (IMAGE_MAX_SIZE * height) / width,
              }
            } else {
              return {
                width: (IMAGE_MAX_SIZE * width) / height,
                height: IMAGE_MAX_SIZE,
              }
            }
          })()

          const canvas = canvasRef.current
          if (!canvas) {
            return
          }
          canvas.width = resize.width
          canvas.height = resize.height
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            return
          }
          ctx.clearRect(0, 0, resize.width, resize.height)
          if (!autoRotateSupport) {
            switch (props.orientation) {
              case 2:
                ctx.transform(-1, 0, 0, 1, resize.width, 0)
                break
              case 3:
                ctx.transform(-1, 0, 0, -1, resize.width, resize.height)
                break
              case 4:
                ctx.transform(1, 0, 0, -1, 0, resize.height)
                break
              case 5:
                ctx.transform(0, 1, 1, 0, 0, 0)
                break
              case 6:
                ctx.transform(0, 1, -1, 0, resize.height, 0)
                break
              case 7:
                ctx.transform(0, -1, -1, 0, resize.height, resize.width)
                break
              case 8:
                ctx.transform(0, -1, 1, 0, 0, resize.width)
                break
            }
          }
          ctx.drawImage(image, 0, 0, resize.width, resize.height)

          const base64 = canvas.toDataURL('image/jpeg')
          props.setImage && props.setImage(base64)
        }
        if (typeof reader.result === 'string') {
          image.src = reader.result
        }
      }
      // 回転情報を取得するライブラリexif-jsでTypescript対応が最適化できていないため、型がanyとなる
      const file: any = event.target.files?.item(0)
      if (file) {
        EXIF.getData(file, () => {
          const orientation = file.exifdata.Orientation || 1
          props.setOrientation && props.setOrientation(orientation)
          reader.readAsDataURL(file)
        })
      }
    },
    [props, autoRotateSupport]
  )

  useEffect(() => {
    const getCanAutoRotate = async (): Promise<void> => {
      setAutoRotateSupport(await canAutoRotate())
    }
    getCanAutoRotate()
  }, [])

  return (
    <ItemLine variableHeight paddingUnnecessary>
      <AreaWrapper>
        <React.Fragment>
          {props.isSupplement && !props.image ? (
            <LeftArea>
              <NotImg>{props.name}</NotImg>
            </LeftArea>
          ) : (
            <LeftArea>
              <StyledImg
                src={props.image ? props.image : props.defaultImage}
                alt={props.name}
              />
            </LeftArea>
          )}
          <RightArea>
            <NameArea>
              <InlineText>
                {!props.isSupplement || props.image ? props.name : <></>}
              </InlineText>
              {props.isRequired ? (
                props.isOptional ? (
                  <RequiredOptional marginLeft="8px" isRequired />
                ) : (
                  <RequiredOptional marginLeft="8px" isOption />
                )
              ) : (
                <RequiredOptional marginLeft="8px" isOption />
              )}
            </NameArea>
            <ActionArea>
              {props.image ? (
                <SecondaryButton
                  bold
                  margin="0"
                  padding="8px"
                  onClick={onClickDelete}
                >
                  <ButtonInnerArea>削除</ButtonInnerArea>
                </SecondaryButton>
              ) : (
                <PrimaryButton
                  bold
                  margin="0"
                  padding="8px"
                  onClick={onClickSelect}
                >
                  <ButtonInnerArea>
                    <CameraIcon
                      color={CLIENT_SETTINGS.colors.textForMainBackground}
                    />
                    選択
                  </ButtonInnerArea>
                </PrimaryButton>
              )}
            </ActionArea>
          </RightArea>
        </React.Fragment>
      </AreaWrapper>
      <HiddenInput
        ref={selectImageRef}
        type="file"
        accept="image/*"
        onChange={onChangeImage}
      />
      <HiddenCanvas ref={canvasRef} id="canvas" width="0" height="0" />
    </ItemLine>
  )
}

export type SelectImageLineProps = {
  image: string
  orientation: number
  defaultImage?: string
  name: string
  isUndamaged?: boolean
  isOptional?: boolean
  isRequired?: boolean
  isSupplement?: boolean
  setImage?: (image: string) => void
  setOrientation?: (orientation: number) => void
}

const AreaWrapper = styled.div`
  display: flex;
  width: 100%;
  margin: 8px 16px 0;
`

const LeftArea = styled.div`
  width: 168px;
  height: 108px;
  margin-right: 12px;
  background: var(--color-background);
`

const RightArea = styled.div`
  flex: 1;
  margin: 20px 0 0;
`

const NotImg = styled.div`
  width: 168px;
  height: 108px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: white;
`

const StyledImg = styled.img`
  width: 100%;
  max-width: 168px;
  height: 100%;
  max-height: 108px;
  object-fit: contain;
`

const NameArea = styled.div`
  min-height: 23px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
`

const ActionArea = styled.div`
  margin: 14px 6px 0;
`

const ButtonInnerArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  > * {
    margin: 0 4px;
  }
`

const HiddenInput = styled.input`
  display: none;
`

const HiddenCanvas = styled.canvas`
  display: none;
`

export default SelectImageLine
